import { type Editor } from '@tiptap/core'
import { getAiToolkit } from '@tiptap-pro/ai-toolkit'

/**
 * Window event dispatched when an AI suggestion review finishes (all suggestions
 * accepted or rejected, via per-suggestion controls or Accept/Reject All). Gem
 * Copilot listens for it to notify the agent of the outcome so it can follow up
 * — e.g. ask what to change when its edits were rejected.
 */
export const SUGGESTION_RESOLVED_EVENT = 'gem-copilot:suggestion-resolved'

export type SuggestionResolvedDetail = {
  // The document whose review resolved. Absent for an editor with no
  // collaborative document.
  documentId?: string
  accepted: number
  rejected: number
  total: number
}

/**
 * Window event dispatched for every accepted or rejected suggestion. A rejection
 * leaves the document unchanged, so Gem Copilot learns of it only here.
 */
export const SUGGESTION_DECIDED_EVENT = 'gem-copilot:suggestion-decided'

export type SuggestionDecidedDetail = { documentId?: string }

type ReviewTally = { accepted: number; rejected: number }

// Per-editor running tally for the active review. Merges per-suggestion
// decisions with a final Accept/Reject All so the completion event reports the
// full outcome. A WeakMap so it never outlives the editor.
const tallies = new WeakMap<Editor, ReviewTally>()

const tallyFor = (editor: Editor): ReviewTally => {
  const existing = tallies.get(editor)
  if (existing) return existing
  const fresh = { accepted: 0, rejected: 0 }
  tallies.set(editor, fresh)
  return fresh
}

// Editors hosted in the monolith iframe must reach the copilot on the parent
// window. Same-origin, so a direct dispatch works; guard in case it isn't.
const dispatchToCopilot = (name: string, detail: object) => {
  window.dispatchEvent(new CustomEvent(name, { detail }))
  if (window.parent !== window) {
    try {
      window.parent.dispatchEvent(new CustomEvent(name, { detail }))
    } catch {
      // Cross-origin parent — nothing more we can do from here.
    }
  }
}

/**
 * Record one or more resolved suggestions for `editor`, and dispatch
 * {@link SUGGESTION_DECIDED_EVENT}. Call AFTER applying the decision to the
 * toolkit so the remaining-suggestion check is accurate. When no suggestions
 * remain, dispatches {@link SUGGESTION_RESOLVED_EVENT} with the merged tally and
 * resets it.
 */
export const recordSuggestionDecisions = (
  editor: Editor,
  documentId: string | undefined,
  decision: 'accepted' | 'rejected',
  count = 1
): void => {
  if (count <= 0) return
  const tally = tallyFor(editor)
  tally[decision] += count
  const decided: SuggestionDecidedDetail = { documentId }
  dispatchToCopilot(SUGGESTION_DECIDED_EVENT, decided)

  if (getAiToolkit(editor).getSuggestions().length > 0) return

  const detail: SuggestionResolvedDetail = {
    documentId,
    accepted: tally.accepted,
    rejected: tally.rejected,
    total: tally.accepted + tally.rejected,
  }
  tallies.delete(editor)
  dispatchToCopilot(SUGGESTION_RESOLVED_EVENT, detail)
}
